import type { Category } from '@scayle/storefront-nuxt'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'

/**
 * Checks if a navigation item should be displayed based on its active state and date range
 */
export function isNavigationItemActive(item: SbMainNavigation): boolean {
  if (!item || !item.active) return false

  const now = new Date()

  if (item.from) {
    // Storyblok stores UTC time, so we parse with 'Z'
    const fromDate = new Date(item.from + 'Z')
    if (now < fromDate) return false
  }

  if (item.till) {
    const tillDate = new Date(item.till + 'Z')
    if (now >= tillDate) return false
  }

  return true
}

/**
 * Validates that navigation data has the required structure
 */
export function validateNavigationData(data: any): boolean {
  return !!(data?.content?.MainNavigation && Array.isArray(data.content.MainNavigation))
}

/**
 * Resolves a Storyblok link to a standardized format, handling:
 * - Empty links
 * - Internal links
 * - Localized paths
 * - Category fallbacks
 */
export function resolveStoryblokLink(args: {
  link?: { cached_url?: string; linktype?: string; url?: string };
  category?: Category;
  defaultPath?: string;
}): string {
  const { link, category, defaultPath } = args
  const { toLocalePath } = useRouteHelpers()

  // Case 1: No link provided or empty internal link - use category path
  if (!link || (link.linktype === 'story' && !link.cached_url?.trim())) {
    if (category) {
      return linkCategoryTo(category).toString()
    }
    return defaultPath ? toLocalePath(defaultPath).toString() : ''
  }

  // Case 2: External link
  if (link.linktype === 'url' && (link.url?.startsWith('http') || link.url?.startsWith('https'))) {
    return link.url
  }

  // Case 3: Internal link with path
  const path = link.cached_url || link.url || ''
  if (path) {
    return toLocalePath(path).toString()
  }

  // Case 4: Fallback to category path if available
  if (category) {
    return linkCategoryTo(category).toString()
  }

  return ''
}

export const resolveNavigationPath = (link: any) => {
  if (!link?.cached_url) return ''

  return link.cached_url.startsWith('/')
    ? link.cached_url
    : '/' + link.cached_url
}
